import { BaseViewStore } from "core/stores";
import { FacilityOrderService, FacilityOrderTemplateService, FacilityOrderStatusService, FacilityOrderPatientValidationService } from "application/facility-order/services";
import { PickTicketHeaderService } from "application/pick-ticket/services";
import { action, observable } from "mobx";
import { QueryUtility } from 'core/utils';
import _ from 'lodash';
import { FacilityOrderTemplateValidationService } from 'application/facility-order/services';
import moment from 'moment';
import { LookupService } from "common/services";
import { ScheduleService } from 'application/schedule/services';
import { LocalPatientService } from "application/patient/services";

class FacilityOrderNewFlowBillingPreviewViewStore extends BaseViewStore {
	@observable accessor facilityOrder = null;
	@observable accessor facilityOrderTemplatesGroupByPatient = [];
	@observable accessor fetchingTemplates = false;
	@observable accessor showPatientList = true;
	@observable accessor currentIndex = null;
	@observable accessor isStatusBarVisible = true;
	@observable accessor showCompleteButton = false;
	@observable accessor pickTicketHeaderStatus;
	@observable accessor sendOrderFacilityOrderStatus;
	@observable accessor facilityOrderStatus;
	@observable accessor isBillingDisabled = false;

	constructor(rootStore) {
        super(rootStore);

        this.rootStore = rootStore;
        this.brightreeBaseUrl = rootStore.applicationStore.app.baasic.settings.brightreeBaseUrl;
        this.facilityOrderId = rootStore.routerStore.routerState.params.id;
        this.facilityOrderService = new FacilityOrderService(rootStore.application.baasic.apiClient);
        this.facilityOrderTemplateService = new FacilityOrderTemplateService(rootStore.application.baasic.apiClient);
        this.facilityOrderStatusService = new FacilityOrderStatusService(rootStore.application.baasic.apiClient);
        this.facilityOrderTemplateValidationService = new FacilityOrderTemplateValidationService(rootStore.application.baasic.apiClient);
        this.facilityOrderPatientValidationService = new FacilityOrderPatientValidationService(rootStore.application.baasic.apiClient);
        this.scheduleService = new ScheduleService(rootStore.application.baasic.apiClient);

        this.queryUtility = new QueryUtility(this.rootStore, async filter => {
			await this.fetchFacilityOrderTemplates(filter);
		}, {});

        this.fetch([this.initialize()]);
    }

	@action.bound
	async initialize(showLoading = true) {
		const response = await this.facilityOrderService.get(this.facilityOrderId, { embed: "Facility,Facility.FacilityAreaOfCares,FacilityOrderStatuses,FacilityOrderPatients" });
		this.facilityOrder = response.data;
		this.isInventoryStepAllowed = (await this.facilityOrderService.isInventoryStepAllowed(this.facilityOrderId)).data;
		const localPatientService = new LocalPatientService(this.rootStore.application.baasic.apiClient);
		const masterPatient = (await localPatientService.getByPatientKey(this.facilityOrder.facility.masterPatientId, { fields: 'id' })).data;
		this.masterPatientId = masterPatient ? masterPatient.id : undefined;
		await this.initializePickTicketHeader();
		this.facilityOrderStatus = this.facilityOrder.facilityOrderStatuses.find(i => i.status.abrv === 'imported');
		this.rootStore.titleStore.setTitle(`Billing - ${this.facilityOrder.facility.name}`);
		this.sendOrderFacilityOrderStatus = this.facilityOrder.facilityOrderStatuses.find(i => i.status.abrv === 'send-order');

		const lookupService = new LookupService(this.rootStore.application.baasic.apiClient);
		this.lookups = await lookupService.getLookups("ImportHeaderStatus,ItemCategory,ImportAction");
		this.enteralItemCategoryId = _.find(this.lookups.itemCategory, s => { return s.abrv === 'enteral'; }).id;
		this.insertImportActionId = this.lookups.importAction.find((s) => s.abrv === 'insert').id;

		if (this.sendOrderFacilityOrderStatus && this.sendOrderFacilityOrderStatus.completedOn) {
			this.fetchingTemplates = showLoading;
			this.queryUtility.fetch();
		}

		const typeResponse = await this.scheduleService.getTypes();
		this.orderType = _.find(typeResponse.item, { abrv: 'ordering' });
	}

	@action.bound
	async initializePickTicketHeader() {
		if (!this.facilityOrder.pickTicketHeaderId) {
			const pickTicketHeaderId = (await this.facilityOrderService.get(this.facilityOrder.id, { fields: 'PickTicketHeaderId' })).data.pickTicketHeaderId;
			this.facilityOrder.pickTicketHeaderId = pickTicketHeaderId;
		}
		if (this.facilityOrder.pickTicketHeaderId) {
			const pickTicketHeaderService = new PickTicketHeaderService(this.rootStore.application.baasic.apiClient);
			const pickTicketHeader = (await pickTicketHeaderService.get(this.facilityOrder.pickTicketHeaderId, { embed: "pickTicketHeaderStatus" })).data;
			this.pickTicketHeaderStatus = pickTicketHeader && pickTicketHeader.pickTicketHeaderStatus && pickTicketHeader.pickTicketHeaderStatus.abrv;
		}
	}

	@action.bound
	async fetchFacilityOrderTemplates(filter) {
		let params = {
			...filter,
			pageSize: 10000,
			facilityOrderId: this.facilityOrder.id,
			embed: 'facilityOrderTemplateValidations,PastFacilityOrderTemplate,FacilityOrderTemplateInitial,FacilityOrderTemplateInitial.OrderingDoctor,FacilityOrderTemplateInitial.ClinicalStatus,FacilityOrderTemplateItems,Patient,SubCategory,ItemCategory,ImportReference,NewFlowStatus,NewFlowInsuranceStatus,NewFlowUSIntakeStatus,ReviewUSIntakeValidations,FacilityOrderTemplateMissingDocuments,FacilityOrderTemplateMissingDocumentInitials,OrderingDoctor,ClinicalStatus,IsFacilityOrderTemplateItemInitial,ImportDetailStatus',
			orderBy: "SubCategory",
			orderDirection: "asc",
			itemCategoryIds: this.facilityOrder.isEnteralWeek ? [this.enteralItemCategoryId] : undefined,
			masterPatientId: this.facilityOrder.isEnteralWeek ? this.masterPatientId : undefined
		}
		const facilityOrderTemplates = (await this.facilityOrderTemplateService.find(params)).data.item;
		let grouped = _.groupBy(facilityOrderTemplates, 'patientId');

		this.facilityOrderTemplatesGroupByPatient = [];
		_.each(grouped, (val, key) => {
			let facilityOrderPatient = this.facilityOrder.facilityOrderPatients && this.facilityOrder.facilityOrderPatients.find(p => p.patientId === key);
			if (val && val.length > 0 && facilityOrderPatient && (!facilityOrderPatient.isNewPatient || (facilityOrderPatient.isNewPatient && val.some(x => x.inactiveReasonId !== 17 && val.inactiveReasonId !== 18)))) {
				var filteredTemplates = facilityOrderPatient.isNewPatient ? val.filter(x => x.inactiveReasonId !== 17 && x.inactiveReasonId !== 18) : val;
				if (filteredTemplates && filteredTemplates.length > 0) {
					let importStatusName = this.getTemplateImportStatus(filteredTemplates, facilityOrderPatient).toUpperCase();
					this.facilityOrderTemplatesGroupByPatient.push({ id: key, item: facilityOrderPatient.isNewPatient ? filteredTemplates : val, patient: val && val[0] && val[0].patient, facilityOrderPatient: facilityOrderPatient, statusStyle: this.getStatusStyle(filteredTemplates, facilityOrderPatient), importStatusName: importStatusName && importStatusName });

				}
			}
		});

		this.isCompleteButtonVisible();
		this.fetchingTemplates = false;
	}

	@action.bound
	getTemplateImportStatus(templates, facilityOrderPatient) {
		if (!templates) {
			return;
		}

		if (templates.some(t => t.importDetailStatus && (t.importDetailStatus.abrv === "new" || t.importDetailStatus.abrv === "inProgress"))) {
			return "awaiting import";
		}
		else if (templates.some(t => t.importDetailStatus && t.importDetailStatus.abrv === "failed")) {
			return "failed";
		}
		else if (templates.some(t => t.importDetailStatus && t.importDetailStatus.abrv === "imported" && (t.facilityOrderTemplateValidations.some(v => !v.isResolved) || (facilityOrderPatient && facilityOrderPatient.facilityOrderPatientValidations.some(v => !v.isResolved))))) {
			return "imported, action needed";
		}
		else if (templates.some(t => t.importDetailStatus && t.importDetailStatus.abrv === "imported")) {
			return "imported";
		}
	}

	@action.bound
	getStatusStyle(templates, facilityOrderPatient) {
		if (!templates) {
			return;
		}

		let isReviewed = templates.every(x => x.isImportReviewed) ? " reviewed" : "";

		if (templates.some(t => t.importDetailStatus && (t.importDetailStatus.abrv === "new" || t.importDetailStatus.abrv === "inProgress"))) {
			return "is-blue" + isReviewed;
		}
		else if (templates.some(t => t.importDetailStatus && t.importDetailStatus.abrv === "failed")) {
			return "is-red" + isReviewed;
		}
		else if (templates.some(t => t.importDetailStatus && t.importDetailStatus.abrv === "imported" && (t.facilityOrderTemplateValidations.some(v => !v.isResolved) || (facilityOrderPatient && facilityOrderPatient.facilityOrderPatientValidations.some(v => !v.isResolved))))) {
			return "is-yellow" + isReviewed;
		}
		else if (templates.some(t => t.importDetailStatus && t.importDetailStatus.abrv === "imported")) {
			return "is-green" + isReviewed;
		}
	}

	@action.bound
	getTemplateStatusStyle(template, facilityOrderPatient) {
		if (!template) {
			return;
		}

		let isReviewed = template.isImportReviewed ? " reviewed" : "";

		if (template.importDetailStatus && (template.importDetailStatus.abrv === "new" || template.importDetailStatus.abrv === "inProgress")) {
			return "is-blue" + isReviewed;
		}
		else if (template.importDetailStatus && template.importDetailStatus.abrv === "failed") {
			return "is-red" + isReviewed;
		}
		// else if (templates.some(t => t.importDetailStatus && t.importDetailStatus.abrv === "imported" && (t.facilityOrderTemplateValidations.some(v => !v.isResolved) || (facilityOrderPatient && facilityOrderPatient.facilityOrderPatientValidations.some(v => !v.isResolved))))) {
		else if (template.importDetailStatus && template.importDetailStatus.abrv === "imported" && (template.facilityOrderTemplateValidations.some(v => !v.isResolved) || (facilityOrderPatient && facilityOrderPatient.facilityOrderPatientValidations.some(v => !v.isResolved)))) {
			return "is-yellow" + isReviewed;
		}
		else if (template.importDetailStatus && template.importDetailStatus.abrv === "imported") {
			return "is-green" + isReviewed;
		}
	}

	@action.bound
	async showPatientTemplates(changeVisibility, index) {
		if (changeVisibility)
			this.showPatientList = !this.showPatientList;

		if (index && index < 0)
			index = 0;

		if (index && index > this.facilityOrderTemplatesGroupByPatient.length - 1)
			index = this.facilityOrderTemplatesGroupByPatient.length - 1;

		this.currentIndex = index;

		if (index != null && index != undefined) {
			let importedTemplates = this.facilityOrderTemplatesGroupByPatient[index].item.filter(x => x.importDetailStatus.abrv === "imported" && (!x.facilityOrderTemplateValidations.some(x => !x.isResolved) && !(this.facilityOrderTemplatesGroupByPatient[index].facilityOrderPatient && this.facilityOrderTemplatesGroupByPatient[index].facilityOrderPatient.facilityOrderPatientValidations.some(v => !v.isResolved))));
			if (importedTemplates.some(x => x)) {
				importedTemplates.forEach(async template => {
					if (!template.isImportReviewed) {
						template.isImportReviewed = true;
						await this.facilityOrderTemplateService.update(template);
					}
				});
			}
			this.isCompleteButtonVisible();
		}
		else {
			this.facilityOrderTemplatesGroupByPatient.forEach(group => {
				group.statusStyle = this.getStatusStyle(group.item, group.facilityOrderPatient)
			});
		}
	}

	@action.bound
	goToBT(patientKey) {
		let url = `${this.brightreeBaseUrl}Patient/frmPatientPersonalRO.aspx?PatientKey=${patientKey}&Edit=0`;
		window.open(`${url}`);
	}

	@action.bound
	openTemplateInBT(templateId) {
		let url = `${this.brightreeBaseUrl}OrderEntry/frmSOOrderRO.aspx?SalesOrderTemplateKey=${templateId}&Edit=0`;
		window.open(`${url}`);
	}

	@action.bound
	openSalesOrderInBT(salesOrderId) {
		let url = `${this.brightreeBaseUrl}OrderEntry/frmSOOrderRO.aspx?SalesOrderKey=${salesOrderId}&ShowAck=-1&Edit=0`;
		window.open(`${url}`);
	}

	@action.bound
	getDiagnosisCodes(facilityOrderTemplate) {
		return ([
			facilityOrderTemplate.dxCode1,
			facilityOrderTemplate.dxCode2,
			facilityOrderTemplate.dxCode3,
			facilityOrderTemplate.dxCode4,
			facilityOrderTemplate.dxCode5,
			facilityOrderTemplate.dxCode6,
			facilityOrderTemplate.dxCode7,
			facilityOrderTemplate.dxCode8,
		].filter(code => code !== null && code !== "")).join(", ");
	}

	@action.bound
	async completeImport() {
		if (!this.facilityOrderStatus || this.facilityOrderStatus.completedOn)
			return;

		try {
			const request = { ...this.facilityOrderStatus, completedOn: new Date(), completedByUserId: this.rootStore.userStore.user.id };
			await this.facilityOrderStatusService.update(request);

			var statusesResponse = await this.facilityOrderStatusService.find({ facilityOrderId: this.facilityOrder.id, embed: "Status,StartedByUser,CompletedByUser" });
			if (statusesResponse.data) {
				this.facilityOrder.facilityOrderStatuses = statusesResponse.data.item;
			}

			let facilityOrderStatus = this.facilityOrder.facilityOrderStatuses.find(i => i.status.abrv === 'imported');
			if (facilityOrderStatus) {
				this.facilityOrderStatus = facilityOrderStatus;
			}

			this.changeStatusBarVisibility();
			this.changeStatusBarVisibility();
			this.isCompleteButtonVisible();
		}
		catch (err) {
			this.rootStore.notificationStore.error("The facility order status has already been updated. Please reload the page.");
		}
	}

	@action.bound
	changeStatusBarVisibility() {
		this.isStatusBarVisible = !this.isStatusBarVisible;
	}

	@action.bound
	async signalRAction(message) {
		if (message.table == "PTH") {
			this.initializePickTicketHeader();
		}
		else if (message.column == "SendOrder") {
			this.facilityOrder.sendOrder = true;
		}
		else if (message.table == "ReloadBilling") {
			await this.initialize(false);
		}
		else if (message.table == "ReloadTemplate") {
			let params = {
				pageSize: 10000,
				facilityOrderId: this.facilityOrder.id,
				embed: "facilityOrderTemplateValidations,PastFacilityOrderTemplate,FacilityOrderTemplateInitial,FacilityOrderTemplateInitial.OrderingDoctor,FacilityOrderTemplateInitial.ClinicalStatus,FacilityOrderTemplateItems,Patient,SubCategory,ItemCategory,ImportReference,NewFlowStatus,NewFlowInsuranceStatus,NewFlowUSIntakeStatus,ReviewUSIntakeValidations,FacilityOrderTemplateMissingDocuments,FacilityOrderTemplateMissingDocumentInitials,OrderingDoctor,ClinicalStatus,IsFacilityOrderTemplateItemInitial,ImportDetailStatus",
				orderBy: "SubCategory",
				orderDirection: "asc",
				ids: message.id,
			};
			const response = await this.facilityOrderTemplateService.find(
				params
			);

			let targetTemplate = response.data.item[0];

			this.facilityOrderTemplatesGroupByPatient.forEach(patientGroup => {
				const template = patientGroup.item.find(template => template.id === targetTemplate.id);
				if (template) {
					Object.assign(template, targetTemplate);
				}
			});

			let matchingTemplate = this.facilityOrderTemplatesGroupByPatient.find(
				group => group.id === targetTemplate.patient.id
			);

			if (matchingTemplate) {
				matchingTemplate.statusStyle = this.getStatusStyle(matchingTemplate.item, matchingTemplate.facilityOrderPatient);
				matchingTemplate.importStatusName = this.getTemplateImportStatus(matchingTemplate.item, matchingTemplate.facilityOrderPatient).toUpperCase();
			}

			this.isCompleteButtonVisible();
		}
	}

	@action.bound
	async reviewTemplate(facilityOrderTemplateId, index) {
		let template = this.facilityOrderTemplatesGroupByPatient[index].item.find(x => x.id === facilityOrderTemplateId);
		template.isImportReviewed = true;
		await this.facilityOrderTemplateService.update(template);
		this.isCompleteButtonVisible();
	}

	@action.bound
	async completeTemplateValidation(facilityOrderTemplateId, index, facilityOrderTemplateValidationId) {
		if (!this.facilityOrderTemplatesGroupByPatient[index].facilityOrderPatient.facilityOrderPatientValidations.some(y => !y.isResolved)) {
			await this.reviewTemplate(facilityOrderTemplateId, index);
		}

		let template = this.facilityOrderTemplatesGroupByPatient[index].item.find(x => x.id === facilityOrderTemplateId);
		let validation = template.facilityOrderTemplateValidations.find(x => x.id === facilityOrderTemplateValidationId);
		if (!validation)
			return;

		validation.isResolved = true;
		validation.resolvedByUserId = this.rootStore.userStore.user.id;
		validation.coreUser = this.rootStore.userStore.user;
		validation.dateResolved = moment();
		await this.facilityOrderTemplateValidationService.bulkUpdate([{ ...validation, coreUser: null }]);
	}

	@action.bound
	async startBillingFlow() {
		if (this.facilityOrderStatus)
			return;

		this.isBillingDisabled = true;

		try {
			const status = this.lookups.importHeaderStatus.find(i => i.abrv === 'imported');
			await this.facilityOrderStatusService.create({ facilityOrderId: this.facilityOrder.id, statusId: status.id, startedOn: new Date(), startedByUserId: this.rootStore.userStore.user.id });

			var statusesResponse = await this.facilityOrderStatusService.find({ facilityOrderId: this.facilityOrder.id, embed: "Status,StartedByUser,CompletedByUser" });
			if (statusesResponse.data) {
				this.facilityOrder.facilityOrderStatuses = statusesResponse.data.item;
			}

			let facilityOrderStatus = this.facilityOrder.facilityOrderStatuses.find(i => i.status.abrv === 'imported');
			this.facilityOrderStatus = facilityOrderStatus;

			this.changeStatusBarVisibility();
			this.changeStatusBarVisibility();
		}
		catch (err) {
			this.rootStore.notificationStore.error("The facility order status has already been updated. Please reload the page.");
			this.isBillingDisabled = false;
		}
		this.isBillingDisabled = false;
	}

	@action.bound
	isCompleteButtonVisible() {
		this.showCompleteButton = this.facilityOrderTemplatesGroupByPatient.every(group => group.item.every(x => x.isImportReviewed)) && (this.facilityOrderStatus && !this.facilityOrderStatus.completedOn);
	}

	@action.bound
	async downloadDeliveryTicket() {
		const pickTicketHeaderService = new PickTicketHeaderService(this.rootStore.application.baasic.apiClient);
		try {
			if (!this.facilityOrder.pickTicketHeaderId) {
				this.facilityOrder.pickTicketHeaderId = (await this.facilityOrderService.get(this.facilityOrder.id, { fields: 'PickTicketHeaderId' })).data.pickTicketHeaderId;
				if (!this.facilityOrder.pickTicketHeaderId) {
					await pickTicketHeaderService.createPickTicketNewFlow({ facilityOrderId: this.facilityOrder.id, setSendOrder: false });
					this.facilityOrder.pickTicketHeaderId = (await this.facilityOrderService.get(this.facilityOrder.id, { fields: 'PickTicketHeaderId' })).data.pickTicketHeaderId;
					this.rootStore.notificationStore.success("Pick ticket successfully created.");
				}
			}
			else {
				const pickTicketHeader = (await pickTicketHeaderService.get(this.facilityOrder.pickTicketHeaderId, { embed: "pickTicketHeaderStatus" })).data;
				if (pickTicketHeader.pickTicketHeaderStatus && pickTicketHeader.pickTicketHeaderStatus.abrv === 'new') {
					await pickTicketHeaderService.refreshNewFlow(this.facilityOrder.pickTicketHeaderId);
				}
			}
		}
		catch (err) {
			if (err.data == "There are no items to include on the Pick Ticket.") {
				return this.rootStore.notificationStore.error('There are no items to include on the Delivery Ticket.');
			}
			else {
				return this.rootStore.notificationStore.error(err.data ? err.data : 'PICK_TICKET_DETAIL.REFRESH.ERROR_REFRESH', err);
			}
		}

		const pickTicketReportDownloadUrl = `${pickTicketHeaderService.getPickDeliveryReportDownloadUrlNewFlow(this.facilityOrder.id)}?contentDisposition=attachment&token=${this.rootStore.authStore.token.tokenUrl}&ttype=${this.rootStore.authStore.token.type}`;
		window.open(pickTicketReportDownloadUrl);
	}

	@action.bound
	async completePatientValidation(facilityOrderPatient, index, facilityOrderPatientValidationId) {
		let validation = facilityOrderPatient.facilityOrderPatientValidations.find(x => x.id === facilityOrderPatientValidationId);
		if (!validation)
			return;

		validation.isResolved = true;
		validation.resolvedByUserId = this.rootStore.userStore.user.id;
		validation.coreUser = this.rootStore.userStore.user;
		validation.dateResolved = moment();
		await this.facilityOrderPatientValidationService.bulkUpdate([{ ...validation, coreUser: null }]);

		this.facilityOrderTemplatesGroupByPatient[index].item.forEach(async template => {
			if (!template.facilityOrderTemplateValidations.some(x => !x.isResolved)) {
				await this.reviewTemplate(template.id, index);
			}
		});

		this.getStatusStyle(this.facilityOrderTemplatesGroupByPatient[index].item, facilityOrderPatient);

		this.showPatientList = !this.showPatientList;
		this.showPatientList = !this.showPatientList;
	}

	@action.bound
	orderHistory() {
		window.open(this.rootStore.routerStore.findRoute("master.app.main.facility-order.facility-order-new-flow.history").pattern.replace(":id", this.facilityOrder.id));
	}

	@action.bound
	async goToFacilityPreview() {
		window.open(`${this.rootStore.routerStore.findRoute('master.app.main.facility.preview').pattern.replace(":id", this.facilityOrder.facilityId)}`);
	}

	@action.bound
	async goToFacilityCalendar() {
		window.open(`${this.rootStore.routerStore.findRoute('master.app.main.schedule.calendar').pattern}?facilityId=${this.facilityOrder.facilityId}&calendarTypeId=${this.orderType.id}`);
	}

	@action.bound
	async downloadFacilityOrder() {
		const downloadUrl = `${(await this.facilityOrderTemplateService.getDownloadNewFlowUrl({ ...this.queryUtility.plainFilter, facilityOrderId: this.facilityOrder.id }))}&contentDisposition=attachment&token=${this.rootStore.authStore.token.tokenUrl
			}&ttype=${this.rootStore.authStore.token.type}`
		window.open(downloadUrl, "_blank")
	}
}

export default FacilityOrderNewFlowBillingPreviewViewStore;